import React, { useEffect } from 'react'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Pagination from 'react-bootstrap/Pagination'

type TableFooterProps = {
  params: {
    page: number,
    perPage: number
  },
  setParams: Function,
  meta: {
    pages: number,
    title: string,
    total: number
  },
  disableJump?: boolean,
  perPageOptions?: number[],
  hidePerPage?: boolean
}

function TableFooter({ meta, params, setParams, disableJump = false, perPageOptions = [10, 25, 50, 100], hidePerPage = false }: TableFooterProps) {
  // Use functional update pattern to avoid stale closure issues
  const setPage = (p: number) => {
    setParams((prev: typeof params) => ({ ...prev, page: p }))
  }
  const setPerPage = (p: number) => {
    // Reset to page 1 whenever per-page changes so the current page is always
    // in range and the user sees results instead of an empty page.
    setParams((prev: typeof params) => ({ ...prev, perPage: p, page: 1 }))
  }

  useEffect(() => {
    if (meta.total < (params.perPage)) {
      setPage(1)
    } else if (meta.total < params.page * (params.perPage)) {
      setPage(meta.pages)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.page, params.perPage, meta.total, meta.pages])

  const { perPage, page } = params

  return (
    <Row data-testid="table-footer">
      {!hidePerPage && (
        <>
          <Col xs="1">
            <Form.Group>
              <Form.Select
                as="select"
                data-testid="per-page-select"
                value={perPage}
                onChange={(e) => setPerPage(parseInt(e.target.value))}
                style={{
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  backgroundImage: 'none',
                  paddingRight: '0'
                }}
              >
                {perPageOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs="2">
            <Form.Label className="fw-light mt-1 me-2">Per Page</Form.Label>
          </Col>
        </>
      )}

      <Col xs={hidePerPage ? "9" : "6"}>
        <Pagination className="mx-auto" style={{ width: '250px' }} data-testid="pagination">
          {!disableJump && (
            <Pagination.First
              data-testid="page-first"
              onClick={() => setPage(1)}
              disabled={page === 1}
            />
          )}
          <Pagination.Prev
            data-testid="page-prev"
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
          />
          {page - 2 > 0 ? (
            <Pagination.Item data-testid={`page-${page - 2}`} onClick={() => setPage(page - 2)}>
              {page - 2}
            </Pagination.Item>
          ) : (
            ''
          )}
          {page - 1 > 0 ? (
            <Pagination.Item data-testid={`page-${page - 1}`} onClick={() => setPage(page - 1)}>
              {page - 1}
            </Pagination.Item>
          ) : (
            ''
          )}
          <Pagination.Item active data-testid="page-active">{page}</Pagination.Item>
          {page + 1 <= meta.pages ? (
            <Pagination.Item data-testid={`page-${page + 1}`} onClick={() => setPage(page + 1)}>
              {page + 1}
            </Pagination.Item>
          ) : (
            ''
          )}
          {page + 2 <= meta.pages ? (
            <Pagination.Item data-testid={`page-${page + 2}`} onClick={() => setPage(page + 2)}>
              {page + 2}
            </Pagination.Item>
          ) : (
            ''
          )}
          <Pagination.Next
            data-testid="page-next"
            onClick={() => setPage(page + 1)}
            disabled={meta.pages <= page}
          />
          {!disableJump && (
            <Pagination.Last
              data-testid="page-last"
              onClick={() => setPage(meta.pages)}
              disabled={meta.pages <= page}
            />
          )}
        </Pagination>
      </Col>
      <Col xs="3">
        <span className="float-end">
          Showing {(page - 1) * (perPage) + 1} to{' '}
          {(meta.total < (perPage)) ? meta.total : (meta.total < page * (perPage)) ? meta.total : page * (perPage)} of {meta.total}
        </span>
      </Col>
    </Row>
  )
}

export default TableFooter
